import React from 'react';
import { Copy, Loading, Modal, SystemText } from '@salesperformanceint/e4shared';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import PageHeader from 'components/general/pageHeader';
import Bullet from 'components/prism/bullet';
import Course from 'components/prism/course';
import Rating from 'components/prism/rating';
import Resource from 'components/prism/resource';
import ObjectiveSummary from 'components/prism/objectiveSummary';
import BlobImage from 'components/blobImage';
import { Overtime } from './components/overtime';
import CourseCard from 'components/prism/courseCard';
import Subheader from 'components/prism/subheader';
import PositiveIndicatorTable from 'components/prism/positiveIndicatorTable';
import { translateCompetency, translateCourse, translatePI } from 'helpers/translations';
import { apiGet } from 'services';
import MetricsGrid from './components/metricsGrid';

export const Behaviour = ({ match }) => {
    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState(null);
    const [metrics, setMetrics] = React.useState([]);
    const [completedCourses, setCompletedCourses] = React.useState([]);
    const [incompletedCourses, setIncompletedCourses] = React.useState([]);
    const [completedObjectives, setCompletedObjectives] = React.useState([]);
    const [incompleteObjectives, setIncompleteObjectives] = React.useState([]);
    const [showRatingsExplanation, setShowRatingsExplanation] = React.useState(false);
    const [metricPeriod, setMetricPeriod] = React.useState('M');

    const { user } = useSelector(state => state.user);
    const { prismMultiColumn } = useSelector(state => state.ui);
    const ratingsRange = useSelector(state => state.user.user.companySettings?.display?.ratingRange || 5);

    const api = {
        loadAll: () => {
            setLoading(true);
            const translateResponse = response => {
                response.behaviour = translateCompetency(user.language_Code, response.behaviour);
                response.positiveIndicators = response.positiveIndicators.map(pi => translatePI(user.language_Code, pi));
                response.courses = response.courses.map(c => translateCourse(user.language_Code, c));
                return response;
            };

            apiGet(`behaviour-details/${match.params.id}/${user.id}`, user.guid, response => {
                setData(translateResponse(response));
                setMetrics(response.metrics);
                setCompletedCourses(response.courses.filter(x => x.progress === 100));
                setIncompletedCourses(response.courses.filter(x => x.progress !== 100));
                setCompletedObjectives(response.objectives.filter(x => x.progress === 100));
                setIncompleteObjectives(response.objectives.filter(x => x.progress !== 100));
                setLoading(false);
            });
        },
        loadMetrics: () => {
            apiGet(
                `behaviour-details-related-metrics/${match.params.id}/${user.id}?period=${metricPeriod}`,
                user.guid,
                response => {
                    setMetrics(response);
                }
            );
        },
    };

    React.useEffect(() => {
        if (loading) {
            return;
        }
        api.loadMetrics();
    }, [user?.id, match.params.id, metricPeriod]);

    React.useEffect(() => {
        if (user?.id && match.params.id) {
            api.loadAll();
        }
    }, [user?.id, match.params.id]);

    if (loading) return <Loading />;

    const text = {
        chart: {
            title: SystemText('BehaviourDetails/Chart/Title'),
            rating: SystemText('BehaviourDetails/Chart/Rating'),
            outstanding: SystemText('BehaviourDetails/Chart/Outstanding'),
        },
        table: {
            title: SystemText('BehaviourDetails/Table/Title'),
        },
        scores: {
            title: SystemText('BehaviourDetails/Scores/Title'),
            intro: SystemText('BehaviourDetails/Scores/Intro', ratingsRange),
            bullet1: SystemText('BehaviourDetails/Scores/Bullet1'),
            bullet2: SystemText('BehaviourDetails/Scores/Bullet2'),
        },
        metrics: {
            title: 'Related Metrics', //SystemText('BehaviourDetails/RelatedMetrics'),
            description: 'These metrics highlight outcomes connected to this behavior and its impact on performance.', //SystemText('BehaviourDetails/RelatedMetricsDescription'),
            periods: {
                week: SystemText('Welcome/Metrics/Week'),
                month: SystemText('Welcome/Metrics/Month'),
                quarter: SystemText('Welcome/Metrics/Quarter'),
            },
        },
        courses: SystemText('BehaviourDetails/Courses'),
        additional: SystemText('BehaviourDetails/Additional'),
        completed: SystemText('BehaviourDetails/Completed'),
        resume: SystemText('Welcome/Pathway/Resume'),
        start: SystemText('Welcome/Pathway/Start'),
    };

    return (
        <>
            <div id="behaviour-page" data-testid="behaviour-page">
                <PageHeader metaTitle={data.behaviour.name} />
                <div className="prism-top-banner mb30 mtm10">
                    {data.behaviour.image ? (
                        <BlobImage src={data.behaviour.image} folder="competency_Image" noholder />
                    ) : (
                        <img src="/dist/img/defaults/behavior-default.jpg" />
                    )}
                </div>
                <div className={prismMultiColumn ? 'two-col-66' : ''}>
                    <div className="">
                        <div className="flex flex-center mb20">
                            <div className="flex-grow">
                                <Bullet type="behavior" />
                            </div>
                            <div className="btn-fake">
                                <i className={`fa fa-flag ${data.behaviour.flagged ? 'fa-solid' : ''}`} />
                            </div>
                        </div>
                        <h1 className="mb10">{data.behaviour.name}</h1>
                        {data.behaviour.description && (
                            <p className="mb10">
                                <Copy value={data.behaviour.description} />
                            </p>
                        )}
                        {data.positiveIndicators.length > 0 && (
                            <>
                                <h3 className="mb10">{text.table.title}</h3>
                                <PositiveIndicatorTable data={data.positiveIndicators} />
                            </>
                        )}
                    </div>
                    <div className={`prism-content-box faded ${!prismMultiColumn ? 'mt20' : ''}`}>
                        <div className="prism-content">
                            {data.overtime.length > 0 && (
                                <>
                                    <h6>{text.chart.title}</h6>
                                    <div style={{ height: '200px', width: '100%' }}>
                                        <Overtime data={data.overtime} />
                                    </div>
                                </>
                            )}
                            <h6 className={data.overtime.length > 0 ? 'mt20' : ''}>
                                <span>{text.chart.rating}</span>
                                <button className="btn btn-link ml10" onClick={() => setShowRatingsExplanation(true)}>
                                    <i className="fa fa-circle-info" />
                                </button>
                            </h6>
                            <div>
                                <Rating level={data.behaviour.level} />
                            </div>

                            {incompleteObjectives.length > 0 && (
                                <>
                                    <h6 className="mt20">{text.chart.outstanding}</h6>
                                    <div className="bullet-list flex flex-wrap">
                                        {incompleteObjectives.map(obj => {
                                            return (
                                                <Link
                                                    key={`metric-${obj.id}`}
                                                    to={`/objectives/${user.id}/objective/${obj.id}`}
                                                >
                                                    <Bullet value={obj.name} style="info round" />
                                                </Link>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <MetricsGrid metrics={metrics} text={text.metrics} setPeriod={setMetricPeriod} period={metricPeriod} />

                {incompletedCourses.length > 0 && (
                    <div className="mt30">
                        <Subheader title={text.courses} count={incompletedCourses.length} />
                        {incompletedCourses.length === 1 ? (
                            <CourseCard course={incompletedCourses[0]} />
                        ) : (
                            <div className={prismMultiColumn ? 'even-col' : 'one-col'}>
                                {incompletedCourses.map(course => (
                                    <Course key={`course-${course.id}`} course={course} padBottom={!prismMultiColumn} />
                                ))}
                            </div>
                        )}
                    </div>
                )}
                {data.resources.length > 0 && (
                    <div className="mt30">
                        <Subheader title={text.additional} count={data.resources.length} />
                        <div className={prismMultiColumn ? 'even-col' : 'one-col'}>
                            {data.resources.map(resource => (
                                <Resource
                                    key={`resource-${resource.id}`}
                                    resource={resource}
                                    padBottom={!prismMultiColumn}
                                />
                            ))}
                        </div>
                    </div>
                )}
                {completedObjectives.length + completedCourses.length > 0 && (
                    <div className="mt30">
                        <Subheader title={text.completed} count={completedObjectives.length + completedCourses.length} />
                        <div className={prismMultiColumn ? 'even-col' : ''}>
                            {completedCourses.map(course => (
                                <Course
                                    key={`completed-course-${course.id}`}
                                    course={course}
                                    padBottom={!prismMultiColumn}
                                />
                            ))}
                            {completedObjectives.map(objective => (
                                <ObjectiveSummary
                                    key={`completed-objective-${objective.id}`}
                                    objective={objective}
                                    padBottom={!prismMultiColumn}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <Modal
                show={showRatingsExplanation}
                close={() => setShowRatingsExplanation(false)}
                title={text.scores.title}
                footer={null}
            >
                <>
                    <p>
                        <Copy value={text.scores.intro} />
                    </p>
                    <ul className="list-of-bullets">
                        <li>
                            <Copy value={text.scores.bullet1} />
                        </li>
                        <li>
                            <Copy value={text.scores.bullet2} />
                        </li>
                    </ul>
                </>
            </Modal>
        </>
    );
};

export default Behaviour;
